import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FiDownload, FiArrowLeft, FiCreditCard } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { getAdminActivitiesPoliciesFullList, getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const emptyFilters = () => ({
    policy_code: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    company_name: '',
    status: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
});

const buildQueryFromFilters = (f) => {
    const out = {};
    Object.keys(f).forEach((key) => {
        if (f[key] && String(f[key]).trim() !== '') {
            out[key] = String(f[key]).trim();
        }
    });
    return out;
};

const buildActivityCombinedAddress = (ai) => {
    const parts = [
        ai.activity_address,
        ai.activity_zip_code,
        ai.activity_city,
        ai.activity_country,
    ]
        .map((v) => (v == null ? '' : String(v).trim()))
        .filter((v) => v !== '');
    return parts.join(', ');
};

const formatActivityTime = (time) => {
    if (time == null || time === '') return '';
    return String(time).substring(0, 5);
};

const policyRowToSheet = (entry) => {
    const pi = entry?.policy_info || entry || {};
    const ci = pi.client_info || entry?.client_info || {};
    const ai = pi.activity_info || entry?.activity_info || {};
    const pr = pi.partner_info || entry?.partner_info || {};
    const activityCombinedAddress = buildActivityCombinedAddress(ai);
    // Même ordre de colonnes que `AdminTripsPoliciesExport` (export séjours)
    return {
        policy_code: pi.policy_code ?? '',
        client_code: ci.client_code ?? '',
        client_first_name: ci.first_name ?? '',
        client_last_name: ci.last_name ?? '',
        status: pi.status ?? '',
        preferred_language: pi.preferred_language ?? '',
        uw_date: pi.uw_date ?? '',
        client_email: ci.email ?? '',
        client_phone: ci.phone_number ?? '',
        company_name: pr.company_name ?? '',
        partner_email: pr.email ?? ai.partner_email ?? '',
        reservation_number: ai.reservation_number ?? '',
        activity_name: ai.name ?? '',
        activity_code: ai.activity_code ?? pi.activity_code ?? '',
        activity_start_date: ai.activity_start_date ?? '',
        activity_start_time: formatActivityTime(ai.activity_start_time),
        activity_end_date: ai.activity_end_date ?? '',
        activity_end_time: formatActivityTime(ai.activity_end_time),
        activity_city: ai.activity_city ?? pi.activity_city ?? '',
        activity_country: ai.activity_country ?? pi.activity_country ?? '',
        supplier_name: ai.supplier_name ?? pi.supplier_name ?? '',
        trip_price: ai.new_trip_price ?? ai.activity_price ?? pi.trip_price ?? '',
        effective_start_date: ai.trip_effective_start_date ?? pi.effective_start_date ?? '',
        effective_end_date: ai.trip_effective_end_date ?? pi.effective_end_date ?? '',
        trip_modified: ai.trip_modified ?? pi.trip_modified ?? '',
        activity_combined_address: activityCombinedAddress,
        price_incl_tsca: pi.price_incl_tsca ?? '',
        orakle_price_ratio_incl_tsca: pi.orakle_price_ratio_incl_tsca ?? '',
        daily_sum_insured_maintain: pi.daily_sum_insured_maintain ?? '',
        daily_sum_insured_cancellation: pi.daily_sum_insured_cancellation ?? '',
        total_sum_insured_maintain: pi.total_sum_insured_maintain ?? '',
        total_sum_insured_cancellation: pi.total_sum_insured_cancellation ?? '',
    };
};

const AdminActivityPoliciesExport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initial = useMemo(
        () => (location.state?.filters && typeof location.state.filters === 'object' ? location.state.filters : {}),
        [location.state]
    );

    const [filters, setFilters] = useState(() => {
        const base = emptyFilters();
        Object.keys(base).forEach((k) => {
            if (initial[k] != null && String(initial[k]).trim() !== '') {
                base[k] = String(initial[k]).trim();
            }
        });
        return base;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);

    useEffect(() => {
        const loadPartners = async () => {
            setPartnersLoading(true);
            try {
                const response = await getAllPartners();
                let partnersList = [];
                if (response?.users && Array.isArray(response.users)) {
                    partnersList = response.users;
                } else if (Array.isArray(response)) {
                    partnersList = response;
                } else if (response?.partners && Array.isArray(response.partners)) {
                    partnersList = response.partners;
                } else if (response?.data && Array.isArray(response.data)) {
                    partnersList = response.data;
                }
                setPartners(partnersList);
            } catch (err) {
                console.error('Erreur lors du chargement des partenaires:', err);
                setPartners([]);
            } finally {
                setPartnersLoading(false);
            }
        };
        loadPartners();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClear = () => {
        setFilters(emptyFilters());
        setError(null);
    };

    const handleExport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const query = buildQueryFromFilters(filters);
            const data = await getAdminActivitiesPoliciesFullList(query);
            const rawList = data?.policies ?? data?.data?.policies ?? (Array.isArray(data) ? data : []);
            const list = Array.isArray(rawList) ? rawList : [];
            if (list.length === 0) {
                setError('Aucune police à exporter pour ces filtres.');
                return;
            }
            const rows = list.map(policyRowToSheet);
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Polices');
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
            XLSX.writeFile(workbook, `activity_policies_export_${stamp}.xlsx`);
        } catch (err) {
            setError(handleApiError(err, 'Erreur lors de l\'export Excel'));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="page-header mb-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                            <div>
                                <h2 className="page-title mb-1">
                                    <FiCreditCard className="me-2" />
                                    Export Excel — Polices activités
                                </h2>
                                <p className="text-muted mb-0">
                                    Données issues de <code>GET /api/policies</code> (filtres optionnels), fichier{' '}
                                    <strong>.xlsx</strong> généré côté navigateur.
                                </p>
                            </div>
                            <Button variant="outline-secondary" className="d-flex align-items-center" onClick={() => navigate('/activity/policies')}>
                                <FiArrowLeft className="me-2" />
                                Retour à la liste
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">Filtres (optionnels)</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control name="policy_code" value={filters.policy_code} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control name="reservation_number" value={filters.reservation_number} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control name="client_first_name" value={filters.client_first_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control name="client_last_name" value={filters.client_last_name} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom de l&apos;entreprise</Form.Label>
                                        <Form.Select
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            disabled={partnersLoading}
                                        >
                                            <option value="">Toutes les entreprises</option>
                                            {partners.map((partner, index) => {
                                                const companyName =
                                                    partner.company_name ||
                                                    `${partner.partner_first_name || partner.first_name || ''} ${partner.partner_last_name || partner.last_name || ''}`.trim() ||
                                                    partner.email ||
                                                    `Partenaire ${index + 1}`;
                                                return (
                                                    <option key={`activity-export-partner-${companyName}-${index}`} value={companyName}>
                                                        {companyName}
                                                    </option>
                                                );
                                            })}
                                        </Form.Select>
                                        {partnersLoading && (
                                            <Form.Text className="text-muted">Chargement des partenaires…</Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Statut</Form.Label>
                                        <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                                            <option value="">Tous les statuts</option>
                                            <option value="Created">Created</option>
                                            <option value="Paid">Paid</option>
                                            <option value="CG confirmed">CG confirmed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_from" value={filters.activity_start_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_start_date_to" value={filters.activity_start_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_from" value={filters.activity_end_date_from} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control type="date" name="activity_end_date_to" value={filters.activity_end_date_to} onChange={handleFilterChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex flex-wrap gap-2 justify-content-end">
                                <Button variant="outline-secondary" onClick={handleClear} disabled={loading}>
                                    Réinitialiser les filtres
                                </Button>
                                <Button variant="success" className="d-flex align-items-center" onClick={handleExport} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" animation="border" />
                                            Export en cours...
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload className="me-2" />
                                            Télécharger Excel
                                        </>
                                    )}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityPoliciesExport;
